import { RaftObserver } from "../application/RAFTs/RaftObserver";
import { RaftInfoEvents } from "../types/events/raft-info";
import { WrapperSentMessage } from "../types/communication-between-apps/wrapper-communication";
import { ConnectionAttemptResults, RaftConnectionMethod } from "../types/raft";
import Connector from "./connectors/Connector";
import Logger from "../services/logger/Logger";
import { RaftConnector, RaftConnEvent, RaftPublishEvent, RaftUpdateEvent } from "@robotical/raftjs";
import { ConnManager, RICLedLcdColours, SystemTypeMarty } from "@robotical/roboticaljs";
import ConnectorFactory from "./connectors/ConnectorFactory";
import ColourSensorManualCalibratorMarty from "./utils/ColourSensorManualCalibratorMarty";
import { getBrowserVersion, getOrGenerateDeviceId, getOSName, isTablet } from "./utils/device-info";
import { CustomBluetoothItem, UserDeviceInfo } from "../types/userDeviceInfo";

const SHOW_LOGS = true;
const TAG = "WrapperAppManager";

export class WrapperAppManager {

    // Connectors 
    private connectors: { [key: string]: Connector } = {};

    // Observers
    private _observers: { [key: string]: Array<RaftObserver> } = {};

    constructor() {
    }

    /**
     * Get connector
     */
    getConnector(raftId: string): Connector | undefined {
        return this.connectors[raftId];
    }

    /**
    * Connect to a RAFT
    *
    * @param {string} method - can be "WebBLE" or "WebSocket"
    * @param {string | object} locator - either a string (WebSocket URL) or an object (WebBLE)
    * @returns Promise<ConnectionAttemptResults>
    *
    */
    async connect(method: RaftConnectionMethod, uuids: string[]): Promise<ConnectionAttemptResults> {
        const connector = await ConnectorFactory.connectToRaftHelper(method, uuids);
        if (!connector) {
            Logger.warn(SHOW_LOGS, TAG, `Failed to connect to RAFT with method: ${method}`);
            return { success: false, reason: "failed" };
        }
        const connectionResults = await connector.connect();
        if (connectionResults.success) {
            this.connectors[connector.id] = connector;
            connectionResults.data = { raftId: connector.id, raftType: connector.type };
        } else {
            Logger.warn(SHOW_LOGS, TAG, `Failed to connect to RAFT with id: ${connector.id} and type: ${connector.type}`);
        }
        Logger.info(SHOW_LOGS, TAG, `Connected to RAFT with id: ${connector.id} and type: ${connector.type}`);
        return connectionResults;
    }

    /**
     * Gets user's device info
     */
    getDeviceInfo(): UserDeviceInfo {
        const info: UserDeviceInfo = {
            deviceId: getOrGenerateDeviceId(),
            deviceName: navigator.userAgent, // User-Agent string as an alternative
            manufacturer: "Webapp",
            model: "N/A",
            systemName: getOSName(),
            systemVersion: navigator.userAgent,
            version: getBrowserVersion(),
            buildNumber: "N/A",
            bundleId: window.location.hostname,
            isEmulator: false,
            isTablet: isTablet(),
        };
        return info;
    }

    /**
     * Get Marty Comms stats
     */
    getCommsStatsMarty(raftId: string) {
        if (this.connectors[raftId]) {
            const raftConnector = this.connectors[raftId].connManager.getConnector();
            const commsStats = raftConnector.getCommsStats();
            return {
                msgRxRate: commsStats.getMsgRxRate(),
                msgRoundtripWorstMs: commsStats.getRTWorstMs(),
                msgRoundtripLastMs: commsStats.getRTLastMs(),
                msgRoundtripBestMs: commsStats.getRTBestMs(),
                msgRetries: commsStats.getRetries()
            };
        }
        return undefined;
    }

    aveFileOnDevice(fileName: string, base64: string) {
        Logger.info(SHOW_LOGS, TAG, `Saving file on device: ${fileName}`);
        try {
            window.localStorage.setItem(fileName, base64);
            return true;
        } catch (error: any) {
            Logger.error(SHOW_LOGS, TAG, `Failed to save file on device: ${error}`);
            return false;
        }

    }

    deleteFiles(fileName: string, _: string) {
        Logger.info(SHOW_LOGS, TAG, `Deleting file: ${fileName}`);
        try {
            window.localStorage.removeItem(`${fileName}`);
            return true;
        } catch (error: any) {
            Logger.error(SHOW_LOGS, TAG, `Failed to delete file: ${error}`);
            return false;
        }
    }

    loadFile(fileName: string, _: string) {
        Logger.info(SHOW_LOGS, TAG, `Loading file: ${fileName}`);
        try {
            const file = window.localStorage.getItem(`${fileName}`);
            if (file) {
                return file;
            }
            return false;
        } catch (error: any) {
            Logger.error(SHOW_LOGS, TAG, `Failed to load file: ${error}`);
            return false;
        }
    }

    /**
     * Re-triggers the connect event which was missed due to the connection logic
     */
    getMissedConnEvent(raftId: any) {
        if (this.connectors[raftId]) {
            this.connectors[raftId].eventHandler("conn", RaftConnEvent.CONN_CONNECTED, "connect", {});
            this.connectors[raftId].publish("conn", RaftConnEvent.CONN_CONNECTED, "connect", {});
        }
    }

    //=====================//
    /* CALIBRATE MARTY  */
    //=====================//
    async calibrateColourSensorMarty(raftId: string) {
        if (this.connectors[raftId]) {
            const raftConnector = this.connectors[raftId].connManager.getConnector();
            const didCalibrate = await ColourSensorManualCalibratorMarty.calibrate(raftConnector);
            return didCalibrate;
        }
        return false;
    }

    //=====================//
    /* END CALIBRATE MARTY */
    //=====================//

    /**
     * Get the RAFT Name
     */
    getRaftName(raftId: string) {
        if (this.connectors[raftId]) {
            return this.connectors[raftId].getRaftName();
        }
        return "";
    }

    /**
     * Verify RAFT
     */
    verifyRaft(ledLcdColours: RICLedLcdColours, raftId: string) {
        if (!this.connectors[raftId]) {
            Logger.warn(SHOW_LOGS, TAG, `verifyRaft: No connector found for raftId: ${raftId}`);
            return false;
        }
        return this.connectors[raftId].verifyRaft(ledLcdColours);
    }

    /**
     * Stop verifying RAFT
     */
    stopVerifyingRaft(isCorrectRIC: boolean, raftId: string) {
        if (!isCorrectRIC) {
            this._removeRICConnector(raftId, 3000);
        }
        return this.connectors[raftId].stopVerifyingRaft(isCorrectRIC);
    }

    /**
     * Disconnect from a RAFT
     */
    disconnect(raftId: string) {
        this._removeRICConnector(raftId, 3000);
        return this.connectors[raftId].disconnect();
    }

    /**
     * Audio streaming
     */
    streamAudio(raftId: string, streamContents: Uint8Array, clearExisting: boolean, duration: number) {
        return this.connectors[raftId].streamAudio(streamContents, clearExisting, duration);
    }

    /**
     * Publish an event to all observers
     * Set in RICConnector as an event listener callback
     */
    publish(
        eventType: string,
        eventEnum: RaftConnEvent | RaftUpdateEvent | RaftPublishEvent | RaftInfoEvents,
        eventName: string,
        eventData: any
    ): void {
        window.WebAppCommunicator.sendMessageNoWait(WrapperSentMessage.RAFT_PUBLISHED_EVENT, { eventType, eventEnum, eventName, eventData });
        if (this._observers.hasOwnProperty(eventType)) {
            for (const observer of this._observers[eventType]) {
                observer.notify(eventType, eventEnum, eventName, eventData);
            }
        }
    }

    /**
     * Send a REST message to the RAFT
     */
    async sendRestMessage(msg: string, params: object | undefined, raftId: string) {
        return this.connectors[raftId].sendRestMessage(msg, params);
    }

    /**
     * Removes marty after a certain time
     * Removes from the martys list after x seconds so that we can still get the last event
     */
    _removeRICConnector(connectorId: string, timeout: number) {
        const martyRemovalTimeout = setTimeout(() => {
            delete this.connectors[connectorId];
            clearTimeout(martyRemovalTimeout);
        }, timeout);
    }

    /**
     * Warranty Service bluetooth scan for device
     * It scans for a device and gets the device object
     * Then it checks if the device is already in the given devices array having CustomBluetoothItem[] type
     * if not, it gets its serial number and adds it to the devices array
     * @returns {Promise<CustomBluetoothItem[]>}
     */
    async scanForPotentialWarrantyDevice(existingDevices: CustomBluetoothItem[]): Promise<CustomBluetoothItem[]> {
        const namesToIgnore = [...existingDevices.map((item) => {
            return { name: item.name || "" }
        })].filter((item) => item.name);

        try {
            const device = await ConnManager.getInstance().getBleDevicesWithGivenNamesToIgnore(namesToIgnore);
            if (!device) {
                Logger.warn(SHOW_LOGS, TAG, "No device was found/selected");
                return [];
            }
            // add the device to the list of devices only if it's not already there
            if (!existingDevices.find((deviceInList) => {
                return deviceInList.id === device.id
            })) {
                // get the serial number of the device
                const connector = ConnManager.getInstance().getConnector();
                await connector.initializeChannel("WebBLE");
                const isConnected = await connector.connect(device);
                if (!isConnected) {
                    Logger.warn(SHOW_LOGS, TAG, `Failed to connect to device: ${device.name}`);
                    return existingDevices;
                }
                const ricSystem = connector.getRaftSystemUtils();
                const systemInfo = await ricSystem.getSystemInfo();
                const serialNumber = systemInfo.SerialNo || "";
                const raftType = systemInfo.SystemName;
                await connector.disconnect();

                const newDevice: CustomBluetoothItem = { id: device.id, name: device.name || "", serialNumber, type: raftType };
                existingDevices.push(newDevice);
                Logger.info(SHOW_LOGS, TAG, `${device.name} added to the list of robots.`);
                return existingDevices;
            } else {
                Logger.warn(SHOW_LOGS, TAG, `${device.name} is already in the list of robots.`);
            }
        } catch (error) {
            Logger.error(SHOW_LOGS, TAG, JSON.stringify(error));
            return existingDevices;
        }
        return existingDevices;
    }

    /**
     * Sends atomic read operation to Marty
     */
    async sendAtomicReadOperationToMarty(raftId: string): Promise<boolean> {
        Logger.info(SHOW_LOGS, TAG, `Sending atomic read operation to Marty with id: ${raftId}`);
        if (this.connectors[raftId]) {
            try {
                const raftConnector = this.connectors[raftId].connManager.getConnector() as RaftConnector; 
                const systemType = raftConnector.getSystemType() as SystemTypeMarty;
                await systemType.getRICServoFaultDetector().atomicReadOperation();
                return true;
            } catch (error) {
                Logger.error(SHOW_LOGS, TAG, `Failed to send atomic read operation to Marty: ${error}`);
            }
        } else {
            Logger.warn(SHOW_LOGS, TAG, `No connector found for raftId: ${raftId}`);
        }
        return false;
    }
}

const wrapperAppManager = new WrapperAppManager();
// @ts-ignore : just for debugging
window.wrapperAppManager = wrapperAppManager;
export default wrapperAppManager;